import { PropsWithChildren } from 'react'
import { PortalModal } from '../../pages/PortalModal'
import { FeatureFlags } from '../../services/FeatureFlags'
import { Dialog, NativeDialogContent } from '@/components/Dialog'
import { env } from '@/services/EnvironmentVariables'
import { cn } from '@/utlis/cn'

export type ModalSettings = {
    title?: string
    alwaysOnTop?: boolean
}

const encodeFrameName = (frameName: string, info: ModalSettings) => {
    return frameName + '[[' + JSON.stringify(info) + ']]'
}

type CrossPlatformDialogProps = PropsWithChildren<{
    className?: string
    /**
     * Overrideable default width and height classes:
     *   - `min-w-[510px]`
     *   - `h-full`
     *
     * For more details, see <PortalModal>
     */
    classNameNative?: string
    /**
     * Overrideable default width and height classes:
     *   - `w-[90vw]`
     *   - `md:w-[510px]`
     *   - `max-h-[80vh]`
     *
     * For more details, see <Dialog>
     */
    classNameBrowser?: string
    show?: boolean
    frameName?: string
    title?: string
    alwaysOnTop?: boolean
    forceBrowserModal?: boolean
    noDefaultScroll?: boolean
    overflow?: boolean
    browserZIndex?: number
    onDefaultClose: () => void
}>

/**
 * Standard cross-platform dialog, provided with the standard defaults:
 * - Background image/color, rounded edge, and padding
 * - Close button in top right corner
 * - Reasonable default width and height, overrideable with tailwind classes
 *   in `classNameNative` and `classNameBrowser`
 * - Overflow scroll
 */
export default function CrossPlatformDialog({
    className,
    classNameNative,
    classNameBrowser,
    show = true,
    frameName = 'Dialog:Default',
    title,
    alwaysOnTop,
    forceBrowserModal,
    noDefaultScroll,
    overflow,
    children,
    onDefaultClose,
    browserZIndex,
}: CrossPlatformDialogProps) {
    if (!show) {
        return null
    }

    if (FeatureFlags.isEnabled('browser-modals') || env.FORCE_BROWSER_MODALS || forceBrowserModal) {
        return (
            <Dialog
                title={title}
                open={show}
                className={cn(className, classNameBrowser)}
                noDefaultScroll={noDefaultScroll}
                onClose={() => onDefaultClose()}
                zIndex={browserZIndex}
            >
                {children}
            </Dialog>
        )
    } else {
        const modalSettings: ModalSettings = {
            title: title,
            alwaysOnTop: alwaysOnTop,
        }

        // encodeFrameName() applies only to dialogs -- those starting with `Dialog:`.
        const frameNameExt = frameName.startsWith('Dialog:') ? encodeFrameName(frameName, modalSettings) : frameName

        // w-0 h-0 overflow-hidden: prevent flashes
        return (
            <div className="w-0 h-0 overflow-hidden">
                <PortalModal
                    frameName={frameNameExt}
                    alwaysOnTop={alwaysOnTop}
                    overflow={overflow}
                    fitContent
                    onClose={onDefaultClose}
                >
                    <NativeDialogContent
                        className={cn(className, classNameNative)}
                        noDefaultScroll={noDefaultScroll}
                        onClose={() => onDefaultClose()}
                    >
                        {children}
                    </NativeDialogContent>
                </PortalModal>
            </div>
        )
    }
}
